import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state as litState } from "lit/decorators.js";
import { ChatApiClient } from "../api-client.js";
import {
  parseChatConfig,
  parseInitialState,
  readJsonScript,
} from "../config.js";
import {
  createChatSessionStore,
  type ChatBootstrapOptions,
  type ChatSessionStore,
} from "../session-store.js";
import {
  buildQuestionPayload,
  buildSourceAwarePayload,
  buildWorkspacePayload,
  parseWorkspaceStartup,
  pushWorkspaceHistoryState,
  replaceWorkspaceHistoryState,
  type WorkspaceStartupState,
} from "../navigation-state.js";
import { normalizeLanguageId } from "../scope-catalog.js";
import { normalizeArticleUrl } from "../source-url.js";
import { applyThemeToDocument } from "../theme-apply.js";
import { mirrorHelpThemeToAfasTheme } from "../theme-bridge.js";
import "./jonas-image-dialog.js";
import type {
  HelpJonasImageDialog,
  ImageDialogRequest,
} from "./jonas-image-dialog.js";
import type {
  ChatAntiforgeryConfig,
  ChatAttachmentInput,
  ChatPageContext,
  ChatState,
  ScopeOption,
  WorkspaceScopeName,
} from "../types.js";
import { parseChatHomeTiles, type ChatHomeTile } from "../home-tiles.js";

@customElement("help-jonas-chat-app")
export class HelpJonasChatApp extends LitElement {
  static override styles = css`
    :host {
      display: block;
      height: 100%;
      min-height: 0;
      background: var(--color-background-900);
      color: var(--color-text-on-background-800);
    }

    .app-frame {
      display: flex;
      height: 100%;
      min-height: 0;
      flex-direction: column;
    }

    help-jonas-chat-shell {
      flex: 1 1 auto;
      min-height: 0;
    }

    .clear-confirm {
      display: flex;
      max-width: 380px;
      flex-direction: column;
      gap: 8px;
      /* Match the dialog's 16px horizontal rhythm so the title and message
         share a left edge with the close button and the footer buttons. */
      padding: 8px 16px 0;
    }

    .clear-confirm-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.3;
      color: var(--color-text-on-background-800);
    }

    .clear-confirm-message {
      margin: 0;
      line-height: 1.5;
      color: var(--color-text-on-background-700);
    }

    .share-dialog {
      display: flex;
      max-width: 460px;
      flex-direction: column;
      gap: 12px;
      /* Share the dialog's 16px horizontal rhythm with the footer buttons. */
      padding: 8px 16px 0;
    }

    .share-dialog-title {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.3;
      color: var(--color-text-on-background-800);
    }

    .share-dialog-message {
      margin: 0;
      line-height: 1.5;
      color: var(--color-text-on-background-700);
    }

    .share-warning {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      padding: 12px 14px;
      background: var(--color-warning-50);
      border: 1px solid var(--color-warning-300);
      border-left: 4px solid var(--color-warning-600);
      border-radius: 8px;
      color: var(--color-warning-700);
    }

    .share-warning__icon {
      flex: 0 0 auto;
      width: 20px;
      height: 20px;
      margin-top: 1px;
      color: var(--color-warning-600);
    }

    .share-warning__text {
      margin: 0;
      line-height: 1.45;
      color: var(--color-warning-700);
    }

    .share-dialog-error {
      margin: 0;
      line-height: 1.45;
      color: var(--color-negative-700);
    }

    .share-link {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .share-link-field {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .share-link-input {
      flex: 1 1 auto;
      min-width: 0;
    }

    .share-copy-button {
      flex: 0 0 auto;
    }

    .share-copy-tooltip {
      flex: 0 0 auto;
    }
  `;

  @property({ attribute: "config-id" })
  accessor configId = "help-chat-config";

  @property({ attribute: "initial-state-id" })
  accessor initialStateId = "chat-workspace-initial-state";

  @property({ attribute: "home-tiles-id" })
  accessor homeTilesId = "chat-home-tiles";

  @litState()
  private accessor chatState: ChatState | null = null;

  @litState()
  private accessor homeTiles: ChatHomeTile[] = [];

  @litState()
  private accessor currentTheme: "light" | "dark" = "light";

  @litState()
  private accessor sourceNavigationDepth = 0;

  @litState()
  private accessor sourceScrollTop = 0;

  @litState()
  private accessor removeHistorySessionToken = "";

  @litState()
  private accessor removeHistoryOpen = false;

  @litState()
  private accessor removeHistoryPending = false;

  @litState()
  private accessor removeHistoryError:
    | ""
    | "not-found"
    | "rate-limited"
    | "retryable-error"
    | "error" = "";

  @litState()
  private accessor shareDialogOpen = false;

  // A `#share=` link waits for the visitor's confirmation before it is redeemed; the token is
  // held here while the question is open (see askToRedeemShare).
  @litState()
  private accessor shareRedeemOpen = false;

  private pendingShareToken = "";

  // The token pair the host page minted; the language switch posts it as a form field.
  private antiforgery: ChatAntiforgeryConfig | null = null;

  @litState()
  private accessor shareUrl = "";

  @litState()
  private accessor shareGenerating = false;

  @litState()
  private accessor shareError = false;

  @litState()
  private accessor shareCopied = false;

  // The message whose conversation prefix the share link will encode. Captured
  // when the dialog opens so the token is only minted once the user confirms.
  private shareMessageId = "";

  private shareCopiedTimer = 0;

  // Drives the interval that keeps the forced "Gekopieerd" tooltip on the share-link copy button
  // open for the copied window (the afas-tooltip hides itself on mousedown/mouseleave otherwise).
  private shareCopiedTooltipTimer = 0;

  private store: ChatSessionStore | null = null;

  private unsubscribe: (() => void) | null = null;

  private themeObserver: MutationObserver | null = null;

  private popstateHandler: ((event: PopStateEvent) => void) | null = null;

  private pagehideHandler: (() => void) | null = null;

  private hashChangeHandler: (() => void) | null = null;

  override connectedCallback(): void {
    super.connectedCallback();
    this.currentTheme = getCurrentTheme();
    this.themeObserver = mirrorHelpThemeToAfasTheme(document.documentElement);

    const config = parseChatConfig(readJsonScript(document, this.configId));
    const initialState = parseInitialState(
      readJsonScript(document, this.initialStateId),
    );
    const homeTilesState = parseChatHomeTiles(
      readJsonScript(document, this.homeTilesId),
    );
    this.homeTiles = homeTilesState.tiles;
    const realApi = new ChatApiClient(config);
    if (config.devTools) {
      // The dev harness (simulateOutage, window.__jonasChatDev) is a chunk of its own that only
      // loads when the server enabled DevTools, so the production bundle carries none of it
      // (pentest finding POA#313168). The import is the one async step before the store exists;
      // the element renders nothing until then, which on a dev host is a few milliseconds.
      // The harness is optional debugging code: if its chunk fails to load, the chat starts
      // on the real API rather than not at all.
      void import("../dev/chat-dev-harness.js").then(
        ({ installChatDevHarness }) =>
          this.start(config, initialState, installChatDevHarness(realApi, config)),
        () => this.start(config, initialState, realApi),
      );
      return;
    }
    this.start(config, initialState, realApi);
  }

  /** Creates the store around the given API and wires the window-level behaviour. */
  private start(
    config: Parameters<typeof createChatSessionStore>[0],
    initialState: Parameters<typeof createChatSessionStore>[1],
    api: Parameters<typeof createChatSessionStore>[2],
  ): void {
    this.store = createChatSessionStore(config, initialState, api);
    this.antiforgery = config.antiforgery ?? null;
    if (config.devTools) {
      (
        window as unknown as { __jonasChatState?: () => unknown }
      ).__jonasChatState = () => {
        const state = this.store!.getState();
        return {
          fallbackMode: state.messages.at(-1)?.fallback?.frozen === false,
          status: state.status,
          sessionToken: state.sessionToken,
          messages: state.messages.map((m) => ({
            role: m.role,
            frozen: m.fallback?.frozen,
            sources: m.sources.length,
          })),
        };
      };
    }
    this.unsubscribe = this.store.subscribe((state) => {
      this.chatState = state;
    });
    const shareToken = this.consumeShareToken();
    const startup = this.readStartupState(window.history.state);
    this.replaceInitialHistoryState(startup);
    this.popstateHandler = (event) => this.handlePopState(event);
    this.pagehideHandler = () => this.saveCurrentSourceScroll();
    // Applying a share link while the app is already open only changes the URL fragment, which
    // never reloads the page (so connectedCallback does not run again). Redeem on the fragment
    // change too, so pasting a `#share=` link into the address bar — or following one in the
    // same tab — works whether or not a chat is already open.
    this.hashChangeHandler = () => this.handleShareHashChange();
    window.addEventListener("popstate", this.popstateHandler);
    window.addEventListener("pagehide", this.pagehideHandler);
    window.addEventListener("hashchange", this.hashChangeHandler);
    if (shareToken) {
      // Ask before redeeming (pentest finding POA#313160): redeeming copies the chat into the
      // visitor's list, and a page elsewhere could otherwise fill that list just by sending the
      // browser here. The normal bootstrap runs underneath; confirming redeems over it.
      this.askToRedeemShare(shareToken);
    }
    const bootstrapOptions: ChatBootstrapOptions =
      this.createBootstrapOptions(startup);
    if (startup.sourceUrl) {
      const prerenderedSourceHtml = this.consumePrerenderedSourceHtml();
      if (prerenderedSourceHtml) {
        bootstrapOptions.prerenderedSourceHtml = prerenderedSourceHtml;
      }
    }
    void this.store.bootstrap(bootstrapOptions);
  }

  override disconnectedCallback(): void {
    this.unsubscribe?.();
    this.themeObserver?.disconnect();
    if (this.popstateHandler) {
      window.removeEventListener("popstate", this.popstateHandler);
      this.popstateHandler = null;
    }
    if (this.pagehideHandler) {
      window.removeEventListener("pagehide", this.pagehideHandler);
      this.pagehideHandler = null;
    }
    if (this.hashChangeHandler) {
      window.removeEventListener("hashchange", this.hashChangeHandler);
      this.hashChangeHandler = null;
    }
    window.clearTimeout(this.shareCopiedTimer);
    window.clearInterval(this.shareCopiedTooltipTimer);
    super.disconnectedCallback();
  }

  // Read a one-time `#share=<token>` from the URL fragment and strip it (so it is not left
  // visible, re-shared, or re-redeemed on refresh). The token rides in the fragment — never the
  // query string — so this multi-use bearer capability is never sent to the server (and so never
  // lands in access logs, proxy logs, or request telemetry) and is never leaked via the Referer
  // header. Returns the token, or null when absent.
  private consumeShareToken(): string | null {
    const rawHash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(rawHash);
    const shareToken = params.get("share");
    if (!shareToken) return null;
    params.delete("share");
    const hash = params.toString();
    const url = `${window.location.pathname}${window.location.search}${hash ? `#${hash}` : ""}`;
    window.history.replaceState(window.history.state, "", url);
    return shareToken;
  }

  // When redeeming a `#share=` link whose URL still carries an article path, move the address bar
  // to the clean workspace URL. The shared chat is not tied to that article: leaving the article
  // path in place makes a later reload re-render the server-side direct-article empty chat, and
  // bootstrap then drops the restored conversation. Rewriting to the workspace URL keeps reloads
  // on a page where bootstrap restores the persisted (redeemed) conversation. No-op when the URL
  // is already the workspace URL (e.g. a share link minted from the workspace).
  private normalizeShareRedeemUrl(): void {
    const state = this.store?.getState();
    if (!state) return;
    const workspaceUrl = this.getWorkspaceUrl();
    if (workspaceUrl === window.location.href) return;
    replaceWorkspaceHistoryState(
      window.history,
      workspaceUrl,
      buildWorkspacePayload({ context: state.context }),
    );
  }

  // Consume a `#share=` fragment and redeem it: the server copies the shared chat into a new chat
  // owned by this visitor and we show that copy. Shared by the initial-load path and the live
  // `hashchange` path. A share link can carry an article path (shared while an article was open),
  // so before redeeming this drops the orphaned server-rendered article pane and rewrites the
  // address bar to the clean workspace URL, so a later reload restores the conversation instead of
  // re-rendering the direct-article empty chat.
  private redeemSharedConversation(shareToken: string): void {
    this.consumePrerenderedSourceHtml();
    this.normalizeShareRedeemUrl();
    // redeemShare closes the source panel, so the in-panel article navigation is reset with
    // it (see handleHistoryResume). Without this the next article in the copied chat shows
    // the old back button while it loads, and opens at the previous article's scroll offset.
    this.sourceNavigationDepth = 0;
    this.sourceScrollTop = 0;
    void this.store?.redeemShare(shareToken);
  }

  // Fires when the URL fragment changes without a page load (e.g. pasting a `#share=` link into
  // the address bar of an already-open workspace). Asks about a freshly added share token and
  // ignores fragment changes that carry no share token.
  private handleShareHashChange(): void {
    const shareToken = this.consumeShareToken();
    if (!shareToken || !this.store) return;
    this.askToRedeemShare(shareToken);
  }

  // Holds the share token while the confirmation dialog is open. Closing the dialog drops it;
  // consumeShareToken already removed it from the URL, so a reload does not ask again.
  private askToRedeemShare(shareToken: string): void {
    this.pendingShareToken = shareToken;
    this.shareRedeemOpen = true;
  }

  private confirmRedeemShare(): void {
    const shareToken = this.pendingShareToken;
    this.closeShareRedeemDialog();
    if (shareToken) {
      this.redeemSharedConversation(shareToken);
    }
  }

  private closeShareRedeemDialog(): void {
    this.pendingShareToken = "";
    this.shareRedeemOpen = false;
  }

  protected override render() {
    return html`
      <div
        class="app-frame"
        @jonas-scope-change=${(
          event: CustomEvent<{
            name: WorkspaceScopeName;
            value: string;
          }>,
        ) => this.handleScopeChange(event)}
        @jonas-theme-toggle=${(
          event: CustomEvent<{ theme?: "light" | "dark" }>,
        ) => this.handleThemeToggle(event)}
        @jonas-image-request=${(event: CustomEvent<ImageDialogRequest>) =>
          this.handleImageRequest(event)}
        @jonas-new-chat=${() => this.handleNewChat()}
      >
        <help-jonas-chat-appbar
          .scope=${this.chatState?.scope ?? null}
          .labels=${this.chatState?.labels ?? null}
          .theme=${this.currentTheme}
        ></help-jonas-chat-appbar>
        <help-jonas-chat-shell
          .state=${this.chatState}
          .homeTiles=${this.homeTiles}
          .sourceCanGoBack=${this.sourceNavigationDepth > 0}
          .sourceScrollTop=${this.sourceScrollTop}
          @jonas-question-submit=${(
            event: CustomEvent<{
              message: string;
              attachments?: ChatAttachmentInput[];
            }>,
          ) => this.handleQuestionSubmit(event)}
          @jonas-source-back=${() => this.handleSourceBack()}
          @jonas-source-close=${() => this.handleSourceClose()}
          @jonas-source-open=${(
            event: CustomEvent<{ url: string; title: string }>,
          ) => this.handleSourceOpen(event)}
          @jonas-source-anchor=${(event: CustomEvent<{ url: string }>) =>
            this.handleSourceAnchor(event)}
          @jonas-feedback-submit=${(
            event: CustomEvent<{
              messageId: string;
              value: "positive" | "negative" | null;
            }>,
          ) => this.handleFeedbackSubmit(event)}
          @jonas-share-request=${(event: CustomEvent<{ messageId: string }>) =>
            this.handleShareRequest(event)}
          @jonas-history-resume=${(event: CustomEvent<{ index: number }>) =>
            this.handleHistoryResume(event)}
          @jonas-history-remove=${(
            event: CustomEvent<{ sessionToken: string }>,
          ) => this.handleHistoryRemove(event)}
          @jonas-history-retry=${() => this.handleHistoryRetry()}
          @jonas-prepared-question-start=${() =>
            this.handlePreparedQuestionStart()}
          @jonas-chat-stop=${() => this.handleChatStop()}
          @jonas-ai-reconnect=${() => this.handleAiReconnect()}
          @jonas-retry-question=${
            (event: CustomEvent<{ question: string }>) =>
              this.handleRetryQuestion(event)
          }
          @jonas-fallback-refine=${(event: CustomEvent<{ query: string }>) =>
            this.handleFallbackRefine(event)}
        >
          <slot name="source" slot="source"></slot>
        </help-jonas-chat-shell>
        <help-jonas-image-dialog></help-jonas-image-dialog>
        ${this.renderRemoveHistoryDialog()}
        ${this.renderShareDialog()}
        ${this.renderShareRedeemDialog()}
      </div>
    `;
  }

  private renderRemoveHistoryDialog() {
    const title = this.getLabel(
      "historyRemoveConfirmTitle",
      "Gesprek verwijderen?",
    );
    const message = this.getLabel(
      "historyRemoveConfirmMessage",
      "Dit gesprek wordt definitief verwijderd. Dit kan niet ongedaan worden gemaakt.",
    );
    const cancelLabel = this.getLabel("historyRemoveCancel", "Annuleren");
    const confirmLabel = this.removeHistoryPending
      ? this.getLabel("historyRemovePending", "Bezig met verwijderen…")
      : this.getLabel("historyRemoveConfirm", "Verwijderen");
    const errorMessage =
      this.removeHistoryError === "rate-limited"
        ? this.getLabel(
            "rateLimited",
            "Er worden te veel verzoeken verstuurd. Wacht even en probeer het opnieuw.",
          )
        : this.removeHistoryError === "not-found"
          ? this.getLabel(
              "historyRemoveNotFound",
              "Dit gesprek bestaat niet meer of is niet beschikbaar.",
            )
          : this.getLabel(
              "historyRemoveError",
              "Het gesprek kon niet worden verwijderd. Probeer het opnieuw.",
            );
    return html`
      <afas-dialog
        size="fit-content"
        disable-movement
        .allowFullscreen=${false}
        ?blocking=${this.removeHistoryPending}
        .open=${this.removeHistoryOpen}
        @dialog-close=${() => this.closeRemoveHistoryDialog()}
      >
        ${this.removeHistoryOpen
          ? html`<div class="clear-confirm">
              <h2 class="clear-confirm-title">${title}</h2>
              <p class="clear-confirm-message">${message}</p>
              ${this.removeHistoryError
                ? html`<afas-alert type="danger">${errorMessage}</afas-alert>`
                : nothing}
            </div>`
          : nothing}
        <afas-button
          slot="footer"
          variant="minimal"
          label=${cancelLabel}
          ?disabled=${this.removeHistoryPending}
          @click=${() => this.closeRemoveHistoryDialog()}
        ></afas-button>
        <afas-button
          slot="footer"
          variant="primary"
          tone="negative"
          label=${confirmLabel}
          ?disabled=${this.removeHistoryPending}
          @click=${() => this.confirmRemoveHistory()}
        ></afas-button>
      </afas-dialog>
    `;
  }

  // Asked before a `#share=` link is redeemed (see connectedCallback).
  private renderShareRedeemDialog() {
    const title = this.getLabel("shareOpenTitle", "Gedeeld gesprek openen?");
    const message = this.getLabel(
      "shareOpenMessage",
      "Iemand heeft een gesprek met je gedeeld. Als je het opent, krijg je een eigen kopie in je gesprekken en kun je daarin verder chatten.",
    );
    const cancelLabel = this.getLabel("shareOpenCancel", "Niet openen");
    const confirmLabel = this.getLabel("shareOpenConfirm", "Gesprek openen");
    return html`
      <afas-dialog
        size="fit-content"
        disable-movement
        .allowFullscreen=${false}
        .open=${this.shareRedeemOpen}
        @dialog-close=${() => this.closeShareRedeemDialog()}
      >
        ${this.shareRedeemOpen
          ? html`<div class="clear-confirm">
              <h2 class="clear-confirm-title">${title}</h2>
              <p class="clear-confirm-message">${message}</p>
            </div>`
          : nothing}
        <afas-button
          slot="footer"
          variant="minimal"
          label=${cancelLabel}
          @click=${() => this.closeShareRedeemDialog()}
        ></afas-button>
        <afas-button
          slot="footer"
          variant="primary"
          label=${confirmLabel}
          @click=${() => this.confirmRedeemShare()}
        ></afas-button>
      </afas-dialog>
    `;
  }

  private renderShareDialog() {
    const title = this.getLabel("shareDialogTitle", "Gesprek delen");
    const explanation = this.getLabel(
      "shareDialogExplanation",
      "Je maakt een deel-link naar dit gesprek tot en met dit antwoord. Iedereen die de link opent, krijgt een eigen kopie van het gesprek en kan daarin zelf verder chatten.",
    );
    const warning = this.getLabel(
      "shareDialogWarning",
      "Let op: iedereen die deze link ontvangt, kan de volledige inhoud van het gesprek zien — alle gestelde vragen tot en met het gedeelde antwoord. Deel de link alleen met mensen die deze informatie mogen inzien.",
    );
    const generatingLabel = this.getLabel(
      "shareDialogGenerating",
      "Bezig met kopiëren…",
    );
    const linkLabel = this.getLabel("shareDialogLinkLabel", "Deel-link");
    const copyLabel = this.getLabel("shareDialogCopy", "Kopieer link");
    const copiedLabel = this.getLabel("shareDialogCopied", "Gekopieerd");
    const closeLabel = this.getLabel("shareDialogClose", "Sluiten");
    const errorMessage = this.getLabel(
      "shareDialogError",
      "Het genereren van de deel-link is mislukt. Probeer het opnieuw.",
    );
    const hasLink = Boolean(this.shareUrl);
    return html`
      <afas-dialog
        size="fit-content"
        disable-movement
        .allowFullscreen=${false}
        .open=${this.shareDialogOpen}
        @dialog-close=${() => this.closeShareDialog()}
      >
        ${this.shareDialogOpen
          ? html`<div class="share-dialog">
              <h2 class="share-dialog-title">${title}</h2>
              <p class="share-dialog-message">${explanation}</p>
              <div class="share-warning" role="note">
                <svg
                  class="share-warning__icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                  />
                </svg>
                <p class="share-warning__text">${warning}</p>
              </div>
              ${this.shareError
                ? html`<p class="share-dialog-error" role="alert">
                    ${errorMessage}
                  </p>`
                : nothing}
              ${hasLink
                ? html`<div class="share-link">
                    <div class="share-link-field">
                      <afas-url-input
                        class="share-link-input"
                        readonly
                        label=${linkLabel}
                        .value=${this.shareUrl}
                      ></afas-url-input>
                      <afas-tooltip
                        class="share-copy-tooltip"
                        content=${this.shareCopied ? copiedLabel : copyLabel}
                      >
                        <afas-button
                          class="share-copy-button"
                          icon-only
                          variant="minimal"
                          tone=${this.shareCopied ? "positive" : "neutral"}
                          state=${this.shareCopied ? "done" : "none"}
                          label=${this.shareCopied ? copiedLabel : copyLabel}
                          @click=${() => this.copyShareLink()}
                        >
                          <afas-icon slot="icon" icon="ContentCopyOutlined"></afas-icon>
                        </afas-button>
                      </afas-tooltip>
                    </div>
                  </div>`
                : nothing}
            </div>`
          : nothing}
        <afas-button
          slot="footer"
          variant="minimal"
          label=${closeLabel}
          @click=${() => this.closeShareDialog()}
        ></afas-button>
        ${hasLink
          ? nothing
          : html`<afas-button
              slot="footer"
              variant="primary"
              ?disabled=${this.shareGenerating}
              label=${this.shareGenerating ? generatingLabel : copyLabel}
              @click=${() => this.generateAndCopyShareLink()}
            >
              <afas-icon slot="icon" icon="ContentCopyOutlined"></afas-icon>
            </afas-button>`}
      </afas-dialog>
    `;
  }

  private handleImageRequest(event: CustomEvent<ImageDialogRequest>): void {
    this.renderRoot
      .querySelector<HelpJonasImageDialog>("help-jonas-image-dialog")
      ?.show(event.detail);
  }

  // The article source render lives in the document light DOM so the bundle
  // stylesheet can style it. Lit only manages this component's shadow root, so
  // the light-DOM child is created here and slot-forwarded down to the source
  // panel, then kept in sync with the active source.
  protected override updated(): void {
    this.syncSourceDocument();
  }

  private syncSourceDocument(): void {
    const source = this.chatState?.activeSource;
    const existing = this.querySelector("help-jonas-source-document");
    // Keep the light-DOM article mounted for the whole lifetime of an active
    // source, including mid-switch when `html` is briefly empty during loading.
    // Removing it would empty the source panel's forwarded `document` slot, so
    // afas-designer-layout drops `has-right-sidebar` and collapses the panel,
    // resizing the layout on every source switch. It is only removed once the
    // source closes (activeSource becomes null).
    if (source) {
      const element =
        existing ?? document.createElement("help-jonas-source-document");
      element.html = source.html;
      element.sourceUrl = source.url;
      if (!existing) {
        element.setAttribute("slot", "source");
        this.appendChild(element);
      }
    } else if (existing) {
      existing.remove();
    }
  }

  // Adopts the server pre-rendered source pane (emitted into the page for
  // crawlers) as the initial article so boot does not trigger a second fetch,
  // then removes it so its article ids do not duplicate the live render.
  private consumePrerenderedSourceHtml(): string {
    const host = document.querySelector("[data-ssr-source-pane]");
    if (!host) return "";
    const pane = host.querySelector("[data-article-source-pane]");
    const html = pane?.outerHTML ?? host.innerHTML;
    host.remove();
    return html;
  }

  private handleQuestionSubmit(
    event: CustomEvent<{
      message: string;
      attachments?: ChatAttachmentInput[];
    }>,
  ): void {
    const question = event.detail.message.trim();
    if (!question) return;

    const state = this.store?.getState();
    if (state && state.status !== "starting" && state.status !== "streaming") {
      this.pushQuestionHistory(question, state);
    }
    void this.store?.submitQuestion(question, event.detail.attachments ?? []);
  }

  private handleSourceBack(): void {
    if (this.sourceNavigationDepth <= 0) return;
    this.saveCurrentSourceScroll();
    this.sourceNavigationDepth -= 1;
    window.history.back();
  }

  private handleSourceClose(): void {
    this.sourceNavigationDepth = 0;
    this.sourceScrollTop = 0;
    this.store?.closeSource();
    const state = this.store?.getState();
    if (state) {
      const targetUrl = this.getWorkspaceUrl();
      replaceWorkspaceHistoryState(
        window.history,
        targetUrl,
        buildWorkspacePayload({ context: state.context }),
      );
    }
  }

  private handleSourceOpen(
    event: CustomEvent<{ url: string; title: string }>,
  ): void {
    const currentSourceUrl = this.store?.getState().activeSource?.url ?? "";
    const nextSourceUrl =
      normalizeArticleUrl(event.detail.url, window.location.href)?.path ?? "";
    const createsSourceStep =
      Boolean(currentSourceUrl && nextSourceUrl) &&
      stripHash(currentSourceUrl) !== stripHash(nextSourceUrl);

    if (createsSourceStep) {
      this.saveCurrentSourceScroll();
      this.sourceScrollTop = 0;
    }

    // A genuine open (no source currently shown) slides the panel in; a switch
    // to another article keeps the panel in place. History/popstate opens go
    // through the store directly and never request the entrance.
    if (!currentSourceUrl) {
      this.renderRoot
        .querySelector("help-jonas-chat-shell")
        ?.requestSourceEntrance();
    }

    void this.store
      ?.openSource(event.detail.url, event.detail.title)
      .then(() => {
        if (createsSourceStep) this.sourceNavigationDepth += 1;
        else if (!currentSourceUrl) this.sourceNavigationDepth = 0;
        this.pushSourceHistory(event.detail.url, event.detail.title);
      });
  }

  private handleSourceAnchor(event: CustomEvent<{ url: string }>): void {
    const articleUrl = normalizeArticleUrl(
      event.detail.url,
      window.location.href,
    );
    const state = this.store?.getState();
    if (!articleUrl || !state?.activeSource) return;

    // Keep the stored source hash-free so re-fetches stay clean; only the
    // browser address bar carries the anchor so deep links scroll on reload.
    const sourceUrl = stripHash(articleUrl.path);
    const sourceTitle = state.activeSource.title;
    replaceWorkspaceHistoryState(
      window.history,
      articleUrl.path,
      buildSourceAwarePayload(sourceUrl, sourceTitle, {
        historySessionToken: state.sessionToken,
        sourceNavigationDepth: this.sourceNavigationDepth,
        context: {
          ...state.context,
          selectedSourceUrl: sourceUrl,
          sourceTitle,
        },
      }),
    );
  }

  private handleFeedbackSubmit(
    event: CustomEvent<{
      messageId: string;
      value: "positive" | "negative" | null;
    }>,
  ): void {
    void this.store?.submitFeedback(event.detail.messageId, event.detail.value);
  }

  // Open the share dialog for this message. The link is not minted yet: the
  // dialog first explains what sharing does and warns that anyone with the link
  // can read the whole conversation, and only mints the token once the user
  // clicks "Link kopiëren". No native prompt/alert is used.
  private handleShareRequest(event: CustomEvent<{ messageId: string }>): void {
    this.shareMessageId = event.detail.messageId;
    this.shareUrl = "";
    this.shareError = false;
    this.shareGenerating = false;
    this.shareCopied = false;
    window.clearTimeout(this.shareCopiedTimer);
    this.shareDialogOpen = true;
  }

  // Primary "Link kopiëren" action: mint an encrypted share token for the
  // conversation up to the shared message, turn it into a shareable URL, and
  // immediately copy it to the clipboard. Errors stay inside the dialog instead
  // of surfacing a native alert. If the clipboard write is rejected, the link
  // is still shown with its hover copy button as a reliable fallback.
  private async generateAndCopyShareLink(): Promise<void> {
    if (this.shareGenerating || !this.shareMessageId) return;
    this.shareGenerating = true;
    this.shareError = false;
    try {
      const result = await this.store?.createShare(this.shareMessageId);
      const token = result?.shareToken ?? "";
      if (!token) {
        this.shareError = true;
        return;
      }
      // Build the link from the clean workspace URL, never the current location
      // path. A chat can be shared while an article is open, and the current path
      // would then bake that article path into the link. The shared chat is
      // independent of the article, and an article-path share link is fragile: the
      // redeemed chat ends up on a direct-article URL whose reload re-renders the
      // server-side empty chat and drops the restored conversation.
      this.shareUrl = `${this.getWorkspaceUrl()}#share=${encodeURIComponent(
        token,
      )}`;
      await this.copyShareLink();
    } catch {
      this.shareError = true;
    } finally {
      this.shareGenerating = false;
    }
  }

  private async copyShareLink(): Promise<void> {
    if (!this.shareUrl) return;
    // Force the "Gekopieerd" tooltip immediately and re-arm it on every (repeated) click,
    // overriding the afas-tooltip's own hover/mousedown hide, then copy. The confirmation shows
    // even if the clipboard write is blocked (e.g. the gesture expired during link generation).
    this.shareCopied = true;
    this.startForcingShareCopiedTooltip();
    window.clearTimeout(this.shareCopiedTimer);
    this.shareCopiedTimer = window.setTimeout(() => {
      this.shareCopied = false;
      this.stopForcingShareCopiedTooltip();
    }, 2000);
    try {
      await navigator.clipboard.writeText(this.shareUrl);
    } catch {
      // Clipboard can be blocked (permissions / insecure context); the read-only
      // field still lets the user select the URL and copy it manually.
    }
  }

  // The afas-tooltip hides on mousedown/mouseleave, so a plain `active` binding does not survive
  // a copy click (or repeated clicks, or the no-hover case after "Link kopieren"). Re-assert
  // `active` each frame while copied so the "Gekopieerd" tooltip stays forced open.
  private setShareCopyTooltipActive(active: boolean): void {
    const tip = this.renderRoot?.querySelector<
      HTMLElement & { active?: boolean }
    >(".share-copy-tooltip");
    if (tip) tip.active = active;
  }

  private startForcingShareCopiedTooltip(): void {
    window.clearInterval(this.shareCopiedTooltipTimer);
    this.setShareCopyTooltipActive(true);
    // A rAF loop is throttled in background/occluded tabs; a short interval re-asserts the forced
    // tooltip reliably so it survives the tooltip's own mousedown/mouseleave hide (and shows
    // without hover after "Link kopieren") until the copied window ends.
    this.shareCopiedTooltipTimer = window.setInterval(() => {
      if (!this.shareCopied) {
        window.clearInterval(this.shareCopiedTooltipTimer);
        return;
      }
      this.setShareCopyTooltipActive(true);
    }, 100);
  }

  private stopForcingShareCopiedTooltip(): void {
    window.clearInterval(this.shareCopiedTooltipTimer);
    this.shareCopiedTooltipTimer = 0;
    this.setShareCopyTooltipActive(false);
  }

  private closeShareDialog(): void {
    this.shareDialogOpen = false;
    window.clearTimeout(this.shareCopiedTimer);
    this.stopForcingShareCopiedTooltip();
  }

  private handleScopeChange(
    event: CustomEvent<{
      name: WorkspaceScopeName;
      value: string;
    }>,
  ): void {
    const group = this.chatState?.scope[event.detail.name];
    const option = [group?.current, ...(group?.options ?? [])].find(
      (candidate): candidate is ScopeOption =>
        !!candidate && candidate.value === event.detail.value,
    );
    const patch = this.createScopePatch(event.detail.name, event.detail.value);
    // A language switch keeps the current conversation (it continues in the new language after the
    // SetLanguage reload); product/country switches reset it because they change the KB scope.
    if (Object.keys(patch).length) {
      this.store?.changeWorkspaceScope(patch, {
        preserveConversation: event.detail.name === "language",
        // Every scope option below navigates to a server-rendered URL, so skip the client-side
        // history refresh: the reload re-fetches it, and an aborted fetch would flash a red
        // "no connection" error in Firefox (issue #116).
        navigating: !!option?.url,
      });
    }

    // Every workspace scope (country, language, product) navigates to its own
    // server-rendered URL so the address bar and all server state (KB scope,
    // home tiles, history) update together. Product previously stayed
    // client-side, which left the URL stale and only took effect on the next
    // reload (e.g. "Nieuwe chat").
    if (option?.url) {
      if (event.detail.name === "language") {
        // /SetLanguage only accepts a POST with the antiforgery token; the server redirects home.
        this.postLanguageSwitch(option.url);
        return;
      }
      window.location.assign(option.url);
    }
  }

  // A plain form, appended to the document so it submits as an ordinary navigation, with the
  // request token as a hidden field.
  private postLanguageSwitch(url: string): void {
    const form = document.createElement("form");
    form.method = "post";
    form.action = url;
    form.hidden = true;
    if (this.antiforgery?.requestToken) {
      const token = document.createElement("input");
      token.type = "hidden";
      token.name = this.antiforgery.fieldName || "__RequestVerificationToken";
      token.value = this.antiforgery.requestToken;
      form.append(token);
    }
    document.body.append(form);
    form.submit();
  }

  private handleHistoryResume(event: CustomEvent<{ index: number }>): void {
    const state = this.store?.getState();
    const entry = state?.history[event.detail.index];
    if (state && entry && !entry.unavailable) {
      // Switching conversations closes the source panel (see resumeHistoryEntry), so the
      // in-panel article navigation is reset with it.
      this.sourceNavigationDepth = 0;
      this.sourceScrollTop = 0;
      this.pushHistoryResumeState(entry, state);
    }
    void this.store?.resumeFromHistory(event.detail.index);
  }

  private handleHistoryRemove(
    event: CustomEvent<{ sessionToken: string }>,
  ): void {
    if (!event.detail.sessionToken) return;
    this.removeHistorySessionToken = event.detail.sessionToken;
    this.removeHistoryOpen = true;
    this.removeHistoryError = "";
  }

  private handleHistoryRetry(): void {
    void this.store?.refreshHistory();
  }

  private closeRemoveHistoryDialog(): void {
    this.removeHistoryOpen = false;
    if (this.removeHistoryPending) return;
    this.removeHistorySessionToken = "";
    this.removeHistoryError = "";
  }

  private async confirmRemoveHistory(): Promise<void> {
    const sessionToken = this.removeHistorySessionToken;
    if (!sessionToken || this.removeHistoryPending) return;

    const store = this.store;
    if (!store) {
      this.removeHistoryError = "error";
      return;
    }

    const wasActive = sessionToken === store.getState().sessionToken;
    this.removeHistoryPending = true;
    this.removeHistoryError = "";
    const result = await store.removeHistoryEntry(sessionToken);
    this.removeHistoryPending = false;
    if (result !== "removed") {
      this.removeHistoryOpen = true;
      this.removeHistoryError = result;
      return;
    }

    this.removeHistoryOpen = false;
    this.removeHistorySessionToken = "";
    if (wasActive) await this.handleNewChat();
  }

  private getLabel(key: string, fallback: string): string {
    return this.chatState?.labels?.[key]?.trim() || fallback;
  }

  private handlePreparedQuestionStart(): void {
    void this.store?.startPreparedQuestion();
  }

  private handleChatStop(): void {
    this.store?.stop();
  }

  private handleAiReconnect(): void {
    void this.store?.reconnectAi();
  }

  private handleRetryQuestion(
    event: CustomEvent<{ question: string }>,
  ): void {
    void this.store?.submitQuestion(event.detail.question);
  }

  private handleFallbackRefine(event: CustomEvent<{ query: string }>): void {
    void this.store?.refineFallbackSearch(event.detail.query);
  }

  private async handleNewChat(): Promise<void> {
    if (!this.chatState?.keepHistory) {
      await this.store?.rotateOffIdentity();
    }
    const targetUrl = this.getWorkspaceUrl();
    this.sourceNavigationDepth = 0;
    this.store?.resetSession();
    const state = this.store?.getState();
    if (state) {
      replaceWorkspaceHistoryState(
        window.history,
        window.location.href,
        buildWorkspacePayload({ context: state.context }),
      );
    }
    if (targetUrl !== window.location.href) {
      window.location.assign(targetUrl);
    }
  }

  private getWorkspaceUrl(): string {
    const scope = this.chatState?.scope;
    // Prefer the selected product's workspace URL so "Nieuwe chat" (which can
    // trigger a full reload when a source article is open) keeps the chosen
    // product instead of falling back to the help-center default. Product
    // switching is client-side only, so the product never reaches the address
    // bar otherwise and a plain help-center reload would reset it server-side.
    const base =
      scope?.product?.current.url ||
      scope?.helpCenter.current.url ||
      window.location.pathname;
    return new URL(base, window.location.href).href;
  }

  private handleThemeToggle(
    event: CustomEvent<{ theme?: "light" | "dark" }>,
  ): void {
    const requestedTheme = event.detail.theme;
    const nextTheme =
      requestedTheme === "dark" || requestedTheme === "light"
        ? requestedTheme
        : getCurrentTheme() === "dark"
          ? "light"
          : "dark";

    try {
      window.localStorage.setItem("theme", nextTheme);
    } catch {
      // Ignore storage failures; the live theme can still change.
    }

    // Mirror the pre-paint theme-bootstrap: also set the inline background + color-scheme
    // (not just data-theme) so the root canvas stays consistent and a later navigation
    // never flashes white after a runtime theme change (issue #116).
    applyThemeToDocument(nextTheme, document.documentElement);
    this.currentTheme = nextTheme;
  }

  private pushSourceHistory(url: string, title: string): void {
    const articleUrl = normalizeArticleUrl(url, window.location.href);
    const state = this.store?.getState();
    if (
      !articleUrl ||
      !state?.activeSource ||
      state.activeSource.url !== articleUrl.path
    ) {
      return;
    }

    const sourceTitle = state.activeSource.title || title;

    pushWorkspaceHistoryState(
      window.history,
      articleUrl.path,
      buildSourceAwarePayload(articleUrl.path, sourceTitle, {
        historySessionToken: state.sessionToken,
        sourceNavigationDepth: this.sourceNavigationDepth,
        context: {
          ...state.context,
          selectedSourceUrl: articleUrl.path,
          sourceTitle,
        },
      }),
    );
  }

  private pushQuestionHistory(question: string, state: ChatState): void {
    const context = {
      ...state.context,
      query: question,
      entryPoint: state.context.entryPoint || "workspace",
    };
    pushWorkspaceHistoryState(
      window.history,
      window.location.href,
      buildQuestionPayload(question, {
        entryPoint: context.entryPoint,
        sourceUrl: context.selectedSourceUrl,
        sourceTitle: context.sourceTitle,
        historySessionToken: state.sessionToken,
        context,
      }),
    );
  }

  private pushHistoryResumeState(
    entry: ChatState["history"][number],
    state: ChatState,
  ): void {
    const entryContext = isRecord(entry.context) ? entry.context : {};
    const question = stringValue(entry.title, state.context.query || "AI chat");
    // The switch closes the source panel, so the pushed entry carries no source — and when
    // one was open the address bar moves off that article path back to the workspace URL.
    const targetUrl = state.activeSource
      ? this.getWorkspaceUrl()
      : window.location.href;
    const context = {
      ...state.context,
      ...entryContext,
      query: question,
      entryPoint: "history-entry",
      selectedSourceUrl: "",
      sourceTitle: "",
    };
    pushWorkspaceHistoryState(
      window.history,
      targetUrl,
      buildQuestionPayload(question, {
        kind: "history",
        entryPoint: "history-entry",
        sourceUrl: "",
        sourceTitle: "",
        historySessionToken: stringValue(entry.sessionToken),
        context,
      }),
    );
  }

  private readStartupState(historyState: unknown): WorkspaceStartupState {
    return parseWorkspaceStartup(
      { url: window.location.href, historyState },
      {
        normalizeSource: (source) =>
          normalizeArticleUrl(source, window.location.href)?.path ?? "",
      },
    );
  }

  private createBootstrapOptions(startup: WorkspaceStartupState) {
    const context = this.createStartupContext(startup);
    return {
      question: startup.question,
      entryPoint: startup.entryPoint,
      sourceUrl: startup.sourceUrl,
      sourceTitle: startup.sourceTitle,
      historySessionToken: this.getStartupHistorySessionToken(startup),
      context,
    };
  }

  private getStartupHistorySessionToken(
    startup: WorkspaceStartupState,
  ): string {
    return (
      startup.questionPayload?.historySessionToken ??
      startup.sourcePayload?.historySessionToken ??
      startup.payload?.historySessionToken ??
      ""
    );
  }

  private createStartupContext(
    startup: WorkspaceStartupState,
  ): Partial<ChatPageContext> {
    const payloadContext = (startup.questionPayload?.context ??
      startup.sourcePayload?.context ??
      startup.payload?.context ??
      {}) as Partial<ChatPageContext>;
    const selectedSourceUrl = startup.sourceUrl;
    return {
      ...payloadContext,
      query: startup.question || payloadContext.query || "",
      entryPoint:
        startup.entryPoint || payloadContext.entryPoint || "workspace",
      selectedSourceUrl,
      sourceTitle: selectedSourceUrl
        ? startup.sourceTitle || payloadContext.sourceTitle || ""
        : "",
    };
  }

  private replaceInitialHistoryState(startup: WorkspaceStartupState): void {
    const context = this.store?.getState().context ?? ({} as ChatPageContext);
    const startupContext = this.createStartupContext(startup);
    const payloadContext = { ...context, ...startupContext };
    const historySessionToken = this.getStartupHistorySessionToken(startup);
    const payload = startup.sourceUrl
      ? buildSourceAwarePayload(startup.sourceUrl, startup.sourceTitle, {
          question: startup.question,
          entryPoint: startup.entryPoint || "source-url",
          historySessionToken,
          context: payloadContext,
        })
      : startup.question
        ? buildQuestionPayload(startup.question, {
            entryPoint: startup.entryPoint || "query-url",
            historySessionToken,
            context: payloadContext,
          })
        : buildWorkspacePayload({ context: payloadContext });

    replaceWorkspaceHistoryState(window.history, window.location.href, payload);
  }

  private handlePopState(event: PopStateEvent): void {
    const startup = this.readStartupState(event.state);
    if (startup.question) {
      this.store?.prepareQuestion(
        startup.question,
        this.createBootstrapOptions(startup),
      );
    } else {
      this.store?.clearPreparedQuestion();
    }

    if (startup.sourceUrl) {
      this.sourceNavigationDepth = startup.sourceNavigationDepth;
      this.sourceScrollTop = startup.sourceScrollTop ?? 0;
      void this.store?.openSource(startup.sourceUrl, startup.sourceTitle);
    } else {
      this.sourceNavigationDepth = 0;
      this.sourceScrollTop = 0;
      this.store?.closeSource();
    }
  }

  private getSourceScrollTop(): number {
    const shell = this.shadowRoot?.querySelector(
      "help-jonas-chat-shell",
    ) as any;
    if (!shell) return 0;
    const panel = shell.shadowRoot?.querySelector(
      "help-jonas-source-panel",
    ) as any;
    if (!panel) return 0;
    const body = panel.shadowRoot?.querySelector(".body") as HTMLElement | null;
    return body?.scrollTop ?? 0;
  }

  private saveCurrentSourceScroll(): void {
    const scrollTop = this.getSourceScrollTop();
    const state = this.store?.getState();
    const activeSource = state?.activeSource;
    if (activeSource?.url && state) {
      const articleUrl = normalizeArticleUrl(
        activeSource.url,
        window.location.href,
      );
      if (articleUrl) {
        replaceWorkspaceHistoryState(
          window.history,
          window.location.href,
          buildSourceAwarePayload(articleUrl.path, activeSource.title, {
            historySessionToken: state.sessionToken,
            sourceNavigationDepth: this.sourceNavigationDepth,
            sourceScrollTop: scrollTop,
            context: {
              ...state.context,
              selectedSourceUrl: articleUrl.path,
              sourceTitle: activeSource.title,
            },
          }),
        );
      }
    }
  }

  private createScopePatch(
    name: WorkspaceScopeName,
    value: string,
  ): Partial<ChatPageContext> {
    if (name === "language") {
      return {
        culture: value,
        languageId: normalizeLanguageId(undefined, value),
      };
    }
    if (name === "product") return { product: value };
    return {};
  }
}

function stringValue(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value;
  if (value === null || typeof value === "undefined") return fallback;
  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function stripHash(value: string): string {
  const hashIndex = value.indexOf("#");
  return hashIndex === -1 ? value : value.slice(0, hashIndex);
}

function getCurrentTheme(): "light" | "dark" {
  return document.documentElement.getAttribute("data-theme") === "dark"
    ? "dark"
    : "light";
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-chat-app": HelpJonasChatApp;
  }
}
